import { assign, fromPromise, setup } from 'xstate';
import { clearError, createErrorAssigner, handleServiceError } from '~/features/alerts/errors';
import type { ServiceError } from '~/features/errors';
import type { Provider } from '~/types/mcv/provider';
import { getData } from '~/utils/service';

/**
 * Provider Service Machine - Data Layer
 *
 * Responsibilities:
 * - Fetches provider data for the current user
 * - Manages provider state for the vault
 * - Provides provider data to components
 *
 * Lifecycle: spawned by the user machine for one provider id and stopped when that session
 * ends. It is fixed to that id for its whole life: a service built for another provider must
 * be replaced, never refreshed into the right one.
 */

// Custom error messages for provider service
const providerErrorMap = {
  401: { code: 'UNAUTHORIZED', message: 'Please log in to access provider data' },
  403: { code: 'FORBIDDEN', message: 'You do not have permission to access this provider' },
  404: { code: 'NOT_FOUND', message: 'Provider not found' },
  500: { code: 'SERVER_ERROR', message: 'Server error occurred while fetching provider data' },
};

export const providerService = setup({
  types: {
    context: {} as {
      provider: Provider | null;
      loading: boolean;
      error: ServiceError | null;
      providerId?: number;
    },
    events: {} as
      | { type: 'GET_PROVIDER' }
      | { type: 'REFRESH' }
      | { type: string; error?: unknown },
    input: {} as { providerId?: number },
  },
  actors: {
    fetchProviderData: fromPromise(async ({ input }: { input: { providerId?: number } }) => {
      // Use /providers/{provider_id} endpoint which returns the current user's provider
      return getData<Provider>('provider', {
        pathParams: { provider_id: input.providerId?.toString() || '' },
      });
    }),
  },
  actions: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- XState action types from error handlers don't perfectly match setup()'s inferred types
    handleServiceError: handleServiceError as any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- XState action types from error handlers don't perfectly match setup()'s inferred types
    clearError: clearError as any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- XState action types from error handlers don't perfectly match setup()'s inferred types
    assignProviderError: createErrorAssigner(providerErrorMap) as any,
  },
}).createMachine({
  id: 'providerService',
  initial: '$_INITIAL',
  context: ({ input }) => ({
    provider: null,
    loading: false,
    error: null,
    providerId: input?.providerId,
  }),
  states: {
    $_INITIAL: {
      // Auto-initialize: immediately start fetching without requiring parent to send event
      always: '$_LOADING',
    },
    $_IDLE: {
      entry: 'clearError',
      on: {
        GET_PROVIDER: {
          target: '$_LOADING',
        },
        REFRESH: {
          target: '$_LOADING',
        },
      },
    },
    $_LOADING: {
      entry: [assign({ loading: true }), 'clearError'],
      invoke: {
        src: 'fetchProviderData',
        input: ({ context }) => ({ providerId: context.providerId }),
        onDone: {
          target: '$_SUCCESS',
          actions: assign({
            provider: ({ event }) => event.output || null,
            loading: () => false,
          }),
        },
        onError: {
          target: '$_ERROR',
          actions: ['handleServiceError', 'assignProviderError', assign({ loading: () => false })],
        },
      },
    },
    $_SUCCESS: {
      entry: 'clearError',
      on: {
        REFRESH: {
          target: '$_LOADING',
        },
      },
    },
    $_ERROR: {
      on: {
        GET_PROVIDER: {
          target: '$_LOADING',
        },
      },
    },
  },
});
